import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../config';
import i18n from '../i18n';

const tr = i18n.t.bind(i18n);

// Ρυθμίσεις αναγνώρισης κλήσης (Yealink Action URL + Remote Phone Book) — μόνο διαχειριστές.
// Δείχνει τα URLs που μπαίνουν στη βάση, δέσιμο με MAC, και ζωντανή λίστα κλήσεων για δοκιμή.
const PhoneSettings = () => {
  const [cfg, setCfg] = useState(null);
  const [calls, setCalls] = useState([]);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${config.API_BASE_URL}/phone/config`);
      setCfg(data); setDraft({});
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
    }
  };
  const loadCalls = async () => {
    try {
      const { data } = await axios.get(`${config.API_BASE_URL}/phone/calls?limit=12`);
      setCalls(data.calls || []);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    load(); loadCalls();
    const t = setInterval(() => { if (document.visibilityState === 'visible') { loadCalls(); } }, 5000);
    return () => clearInterval(t);
  }, []);

  if (forbidden) return <p style={{ opacity: 0.7 }}>{tr('Admins only.')}</p>;
  if (!cfg) return null;

  const val = (k) => (draft[k] !== undefined ? draft[k] : cfg[k]);
  const dirty = Object.keys(draft).length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${config.API_BASE_URL}/phone/config`, draft);
      setCfg(data); setDraft({});
      toast.success(tr('Settings saved.'), { autoClose: 2500 });
    } catch (e) {
      toast.error(e?.response?.data?.error || tr('Save failed.'));
    } finally { setSaving(false); }
  };
  const rotate = async () => {
    if (!window.confirm(tr('Generate a new key? The URLs already entered in the phone base will stop working until you update them.'))) return;
    try {
      const { data } = await axios.post(`${config.API_BASE_URL}/phone/config/rotate`);
      setCfg(data); setDraft({});
      toast.success(tr('New key generated. Update the URLs in the phone base.'));
    } catch (e) { toast.error(e?.response?.data?.error || tr('Save failed.')); }
  };
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.info(tr('Copied.'), { autoClose: 1200 }); }
    catch { window.prompt(tr('Copy this URL:'), text); }
  };
  const fmt = (d) => (d ? new Date(d).toLocaleString('el-GR') : '—');
  const eventLabel = { incoming: tr('Ringing'), established: tr('Answered'), terminated: tr('Ended'), missed: tr('Missed'), outgoing: tr('Outgoing') };

  const UrlBox = ({ label, url, hint }) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
      {hint && <div style={{ fontSize: 11, opacity: 0.6 }}>{hint}</div>}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
        <input readOnly value={url} onFocus={(e) => e.target.select()}
          style={{ flex: 1, fontSize: 11, padding: 6, borderRadius: 6, border: '1px solid #ccc', fontFamily: 'monospace' }} />
        <button type="button" className="rlToolbarBtn" style={{ padding: '6px 10px' }} onClick={() => copy(url)}>{tr('Copy')}</button>
      </div>
    </div>
  );

  return (
    <div style={{ marginTop: 26, maxWidth: 1100 }}>
      <h3>📞 {tr('Caller ID (Yealink)')}</h3>
      <p style={{ fontSize: 13.5, opacity: 0.8, maxWidth: 760 }}>
        {tr('When a client calls, the handset shows their name from your client list and EstiaSync shows who is calling. In the reservation and client forms, the «Last call» button pulls the caller in. Works with Yealink IP phones (W-series DECT bases and T-series desk phones).')}
      </p>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ border: '1px solid #ddd', borderRadius: 10, padding: '10px 14px', flex: '2 1 460px' }}>
          <b style={{ display: 'block', marginBottom: 8 }}>{tr('1. Phone base → Features → Action URL')}</b>
          <UrlBox label={tr('Incoming Call')} url={cfg.eventUrls.incoming} hint={tr('Required. This is what makes «who is calling» work.')} />
          <UrlBox label={tr('Call Established')} url={cfg.eventUrls.established} />
          <UrlBox label={tr('Call Terminated')} url={cfg.eventUrls.terminated} />
          <UrlBox label={tr('Missed Call')} url={cfg.eventUrls.missed} hint={tr('Optional: keeps a record of missed calls.')} />
          <b style={{ display: 'block', margin: '14px 0 8px' }}>{tr('2. Phone base → Directory → Remote Phone Book')}</b>
          <UrlBox label={tr('Remote Phone Book URL')} url={cfg.phonebookUrl}
            hint={tr('Name it «EstiaSync». On the base, enable «remote phone book lookup for incoming calls» so the handset shows the client name.')} />
        </div>

        <div style={{ border: '1px solid #ddd', borderRadius: 10, padding: '10px 14px', flex: '1 1 280px', minWidth: 260 }}>
          <b style={{ display: 'block', marginBottom: 8 }}>{tr('Options')}</b>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginBottom: 8 }}>
            <input type="checkbox" checked={!!val('enabled')} onChange={(e) => setDraft((p) => ({ ...p, enabled: e.target.checked }))} />
            {tr('Caller ID enabled')}
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, marginBottom: 8 }}>
            <input type="checkbox" checked={!!val('phonebook_latin')} onChange={(e) => setDraft((p) => ({ ...p, phonebook_latin: e.target.checked }))} />
            {tr('Phone book in Latin characters')}
          </label>
          <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 10 }}>{tr('Turn on if the handset shows boxes instead of Greek letters.')}</div>
          <label style={{ fontSize: 14 }}>{tr('Base MAC address (optional)')}</label>
          <input value={val('mac') || ''} placeholder="44DBD2EC18B7"
            onChange={(e) => setDraft((p) => ({ ...p, mac: e.target.value }))}
            style={{ width: '100%', padding: 6, borderRadius: 6, border: '1px solid #ccc', fontFamily: 'monospace', marginTop: 4 }} />
          <div style={{ fontSize: 11, opacity: 0.6 }}>{tr('Printed on the label under the base. If set, only this base is accepted.')}</div>
          {dirty && (
            <button className="rlToolbarBtn rlToolbarBtnPrimary" disabled={saving}
              style={{ marginTop: 10, padding: '8px 18px' }} onClick={save}>
              {saving ? tr('Sending...') : tr('Save settings')}
            </button>
          )}
          <hr style={{ margin: '14px 0', border: 0, borderTop: '1px solid #eee' }} />
          <div style={{ fontSize: 12.5 }}>
            <div>{tr('Last event from the base')}: <b>{fmt(cfg.last_event_at)}</b>{cfg.last_event_ip ? ` · ${cfg.last_event_ip}` : ''}</div>
            <div>{tr('Phone book last downloaded')}: <b>{fmt(cfg.phonebook_fetched_at)}</b></div>
          </div>
          <button type="button" className="rlToolbarBtn" style={{ marginTop: 12, padding: '6px 12px' }} onClick={rotate}>
            {tr('Generate new key')}
          </button>
        </div>
      </div>

      <div style={{ border: '1px solid #ddd', borderRadius: 10, padding: '10px 14px', marginTop: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <b>{tr('Recent calls')}</b>
          <span style={{ fontSize: 12, opacity: 0.6 }}>{tr('Refreshes every 5 seconds. Call the shop from your mobile to test.')}</span>
          <button type="button" className="rlToolbarBtn" style={{ marginLeft: 'auto', padding: '4px 10px' }} onClick={loadCalls}>{tr('Refresh')}</button>
        </div>
        {calls.length === 0 ? (
          <p style={{ fontSize: 13, opacity: 0.6 }}>{tr('No calls recorded yet.')}</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
            <thead>
              <tr style={{ textAlign: 'left', opacity: 0.7 }}>
                <th style={{ padding: 4 }}>{tr('Time')}</th><th style={{ padding: 4 }}>{tr('Number')}</th>
                <th style={{ padding: 4 }}>{tr('Client')}</th><th style={{ padding: 4 }}>{tr('Status')}</th><th style={{ padding: 4 }}>{tr('Handset')}</th>
              </tr>
            </thead>
            <tbody>
              {calls.map((c) => (
                <tr key={c.id} style={{ borderTop: '1px solid #eee' }}>
                  <td style={{ padding: 4, whiteSpace: 'nowrap' }}>{fmt(c.createdAt)}</td>
                  <td style={{ padding: 4, fontFamily: 'monospace' }}>{c.number || c.numberRaw || '—'}</td>
                  <td style={{ padding: 4 }}>{c.client ? `${c.client.lastname || ''} ${c.client.firstname || ''} ★${c.client.rate} (${c.client.reservations || 0})` : <i style={{ opacity: 0.6 }}>{tr('Unknown')}</i>}</td>
                  <td style={{ padding: 4 }}>{eventLabel[c.event] || c.event}</td>
                  <td style={{ padding: 4 }}>{c.handset || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default PhoneSettings;
